import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { USER_MANAGEMENT_API_CONFIG, UserManagementConfig } from '../config';
import {
  Organization,
  OrganizationPopulated,
  CreateOrganizationDto,
  UpdateOrganizationDto,
  OrganizationQueryParams,
  OrganizationResponse,
  OrganizationListResponse,
  CreateOrganizationResponse,
  UpdateOrganizationResponse,
  DeleteOrganizationResponse
} from '../models';

/**
 * Service for managing organizations via the User Management API
 * 
 * This service provides comprehensive methods for:
 * - CRUD operations on organizations
 * - Organization membership management
 * - Organization discovery by code
 * - User access control (Admin, Editor, Reader roles)
 * 
 * @remarks
 * **Authentication:** All endpoints require authentication via Bearer token.
 * Authentication is handled by HTTP interceptors provided by the consuming application.
 * 
 * **Access Control:**
 * - Only organization creators (owners) can delete organizations
 * - Organization members can view and update organizations they belong to
 * - Non-members cannot access organizations
 * 
 * **Business Rules:**
 * - Organization names must be unique per user
 * - Organization codes are auto-generated if not provided
 * - Organizations are sorted by creation date (newest first)
 * 
 * @example
 * ```typescript
 * // Inject the service
 * constructor(private organizationService: OrganizationService) {}
 * 
 * // Create an organization
 * this.organizationService.createOrganization({
 *   name: 'My Organization',
 *   orgCode: 'MY-ORG'
 * }).subscribe(response => {
 *   console.log('Created:', response.data);
 * });
 * 
 * // Get all organizations where user is creator
 * this.organizationService.getAllOrganizations().subscribe(response => {
 *   console.log('Organizations:', response.data);
 * });
 * 
 * // Find organizations by code
 * this.organizationService.findByCode('MY-ORG').subscribe(orgs => {
 *   console.log('Found:', orgs);
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class OrganizationService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(USER_MANAGEMENT_API_CONFIG);
  private readonly apiUrl: string;

  constructor() {
    this.apiUrl = `${this.config.apiBaseUrl}/api/organizations`;
    this.logDebug('OrganizationService initialized', { apiUrl: this.apiUrl });
  }

  // ============================================================================
  // CREATE OPERATIONS
  // ============================================================================

  /**
   * Create a new organization
   * 
   * @param dto - Organization creation data
   * @returns Observable of API response with created organization
   * 
   * @throws {Error} 400 - Invalid request data (name validation failed)
   * @throws {Error} 401 - Unauthorized (missing or invalid authentication)
   * @throws {Error} 409 - Conflict (organization name already exists for this user)
   * 
   * @remarks
   * **Request Requirements:**
   * - `name` is required (2-100 characters)
   * - `orgCode` is optional (auto-generated if omitted)
   * - Organization name must be unique per user
   * 
   * **Response Details:**
   * - Returns populated organization with creator details
   * - Creator is automatically added as the first Admin member
   * - Created organization includes the creator in the members array
   * 
   * @example
   * ```typescript
   * this.organizationService.createOrganization({
   *   name: 'Tech Company',
   *   orgCode: 'TECH-CO'
   * }).subscribe({
   *   next: (response) => {
   *     console.log('Organization created:', response.data);
   *     console.log('Organization ID:', response.data._id);
   *     console.log('Creator:', response.data.createdBy.name);
   *     console.log('Message:', response.message);
   *   },
   *   error: (error) => {
   *     if (error.status === 409) {
   *       console.error('Organization name already exists');
   *     }
   *   }
   * });
   * ```
   */
  createOrganization(dto: CreateOrganizationDto): Observable<CreateOrganizationResponse> {
    this.logDebug('Creating organization', dto);
    
    return this.http.post<CreateOrganizationResponse>(this.apiUrl, dto).pipe(
      map(response => {
        this.logDebug('Organization created successfully', { orgId: response.data._id });
        return response;
      }),
      catchError(error => this.handleError('createOrganization', error))
    );
  }

  // ============================================================================
  // READ OPERATIONS
  // ============================================================================

  /**
   * Get all organizations with optional filtering
   * 
   * @param params - Query parameters for filtering
   * @returns Observable of API response with array of organizations
   * 
   * @throws {Error} 401 - Unauthorized (missing or invalid authentication)
   * 
   * @remarks
   * **Behavior:**
   * - Without params: Returns all organizations where user is the creator
   * - With `createdByMe: true`: Returns only organizations created by user
   * - With `createdByMe: false`: Returns organizations where user is a member
   * 
   * **Response Details:**
   * - Organizations are sorted by creation date (newest first)
   * - Returns populated organizations with creator and member details
   * - Count field indicates total number of organizations returned
   * 
   * @example
   * ```typescript
   * // Get all organizations where user is creator
   * this.organizationService.getAllOrganizations().subscribe(response => {
   *   console.log('Count:', response.count);
   *   console.log('Organizations:', response.data);
   * });
   * 
   * // Get organizations where user is a member
   * this.organizationService.getAllOrganizations({ 
   *   createdByMe: false 
   * }).subscribe(response => {
   *   console.log('Member of:', response.data);
   * });
   * ```
   */
  getAllOrganizations(params?: OrganizationQueryParams): Observable<OrganizationListResponse> {
    this.logDebug('Getting all organizations', params);
    
    const httpParams = this.buildHttpParams(params);
    
    return this.http.get<OrganizationListResponse>(this.apiUrl, { params: httpParams }).pipe(
      map(response => {
        this.logDebug('Organizations retrieved', { count: response.count });
        return response;
      }),
      catchError(error => this.handleError('getAllOrganizations', error))
    );
  }

  /**
   * Get a single organization by ID with full details
   * 
   * @param id - Organization ID (MongoDB ObjectId)
   * @returns Observable of API response with populated organization
   * 
   * @throws {Error} 400 - Bad Request (invalid organization ID format)
   * @throws {Error} 401 - Unauthorized (missing or invalid authentication)
   * @throws {Error} 403 - Forbidden (user is not a member of the organization)
   * @throws {Error} 404 - Not Found (organization does not exist)
   * 
   * @remarks
   * **Access Control:**
   * - User must be a member of the organization to access it
   * - Non-members will receive a 403 Forbidden error
   * 
   * **Response Details:**
   * - Returns fully populated organization
   * - Creator details are populated (id, name, email)
   * - All members are populated with full user details and roles
   * - Includes all organization metadata
   * 
   * @example
   * ```typescript
   * this.organizationService.getOrganizationById('507f1f77bcf86cd799439011')
   *   .subscribe({
   *     next: (response) => {
   *       const org = response.data;
   *       console.log('Organization:', org.name);
   *       console.log('Code:', org.orgCode);
   *       console.log('Creator:', org.createdBy.name);
   *       console.log('Members:', org.members.length);
   *       
   *       // Access member details
   *       org.members.forEach(member => {
   *         console.log(`${member.user.name} - ${member.role}`);
   *       });
   *     },
   *     error: (error) => {
   *       if (error.status === 403) {
   *         console.error('Access denied - not a member');
   *       }
   *     }
   *   });
   * ```
   */
  getOrganizationById(id: string): Observable<OrganizationResponse> {
    this.logDebug('Getting organization by ID', { id });
    
    return this.http.get<OrganizationResponse>(`${this.apiUrl}/${id}`).pipe(
      map(response => {
        this.logDebug('Organization retrieved', { orgId: response.data._id });
        return response;
      }),
      catchError(error => this.handleError('getOrganizationById', error))
    );
  }

  // ============================================================================
  // UPDATE OPERATIONS
  // ============================================================================

  /**
   * Update an existing organization
   * 
   * @param id - Organization ID to update
   * @param dto - Updated organization data
   * @returns Observable of API response with updated organization
   * 
   * @throws {Error} 400 - Bad Request (invalid ID or validation failed)
   * @throws {Error} 401 - Unauthorized (missing or invalid authentication)
   * @throws {Error} 403 - Forbidden (user is not a member of the organization)
   * @throws {Error} 404 - Not Found (organization does not exist)
   * @throws {Error} 409 - Conflict (organization name already exists for this user)
   * 
   * @remarks
   * **Access Control:**
   * - User must be a member of the organization to update it
   * - Any member can update the organization (not restricted to creator)
   * 
   * **Update Rules:**
   * - `name` can be updated (must remain unique per user)
   * - `orgCode` can be updated (must remain unique globally)
   * - All fields are optional in the update DTO
   * 
   * **Response Details:**
   * - Returns populated organization with updated data
   * - Includes success message
   * 
   * @example
   * ```typescript
   * this.organizationService.updateOrganization(
   *   '507f1f77bcf86cd799439011',
   *   { name: 'Updated Organization Name' }
   * ).subscribe({
   *   next: (response) => {
   *     console.log('Updated:', response.data.name);
   *     console.log('Message:', response.message);
   *   },
   *   error: (error) => {
   *     if (error.status === 409) {
   *       console.error('Name already in use');
   *     }
   *   }
   * });
   * ```
   */
  updateOrganization(id: string, dto: UpdateOrganizationDto): Observable<UpdateOrganizationResponse> {
    this.logDebug('Updating organization', { id, dto });
    
    return this.http.put<UpdateOrganizationResponse>(`${this.apiUrl}/${id}`, dto).pipe(
      map(response => {
        this.logDebug('Organization updated successfully', { orgId: response.data._id });
        return response;
      }),
      catchError(error => this.handleError('updateOrganization', error))
    );
  }

  // ============================================================================
  // DELETE OPERATIONS
  // ============================================================================

  /**
   * Delete an organization permanently
   * 
   * @param id - Organization ID to delete
   * @returns Observable of API response confirming deletion
   * 
   * @throws {Error} 400 - Bad Request (invalid organization ID format)
   * @throws {Error} 401 - Unauthorized (missing or invalid authentication)
   * @throws {Error} 403 - Forbidden (user is not the creator/owner)
   * @throws {Error} 404 - Not Found (organization does not exist)
   * 
   * @remarks
   * **Access Control:**
   * - Only the organization creator (owner) can delete it
   * - Other members, including admins, cannot delete the organization
   * 
   * **Deletion Impact:**
   * - Organization is permanently removed from the database
   * - Consider the impact on related entities (blog posts, groups, etc.) before deleting
   * - This operation cannot be undone
   * 
   * @example
   * ```typescript
   * this.organizationService.deleteOrganization('507f1f77bcf86cd799439011')
   *   .subscribe({
   *     next: (response) => {
   *       console.log('Success:', response.message);
   *       // Navigate away or update UI
   *     },
   *     error: (error) => {
   *       if (error.status === 403) {
   *         console.error('Only the owner can delete this organization');
   *       }
   *     }
   *   });
   * ```
   */
  deleteOrganization(id: string): Observable<DeleteOrganizationResponse> {
    this.logDebug('Deleting organization', { id });
    
    return this.http.delete<DeleteOrganizationResponse>(`${this.apiUrl}/${id}`).pipe(
      map(response => {
        this.logDebug('Organization deleted successfully', { id });
        return response;
      }),
      catchError(error => this.handleError('deleteOrganization', error))
    );
  }

  // ============================================================================
  // CONVENIENCE METHODS
  // ============================================================================

  /**
   * Get organizations data without the API response wrapper
   * 
   * @param params - Query parameters
   * @returns Observable of organizations array
   * 
   * @remarks
   * This convenience method extracts just the data array from the API response.
   * 
   * @example
   * ```typescript
   * this.organizationService.getOrganizationsData().subscribe(orgs => {
   *   console.log('My organizations:', orgs.length);
   *   orgs.forEach(org => {
   *     console.log(`${org.name} (${org.orgCode})`);
   *   });
   * });
   * ```
   */
  getOrganizationsData(params?: OrganizationQueryParams): Observable<OrganizationPopulated[]> {
    return this.getAllOrganizations(params).pipe(
      map(response => response.data || [])
    );
  }

  /**
   * Get organization data without the API response wrapper
   * 
   * @param id - Organization ID
   * @returns Observable of organization
   * 
   * @remarks
   * This convenience method extracts just the data from the API response,
   * making it easier to work with in components.
   * 
   * @example
   * ```typescript
   * this.organizationService.getOrganizationData('507f1f77bcf86cd799439011')
   *   .subscribe(org => {
   *     console.log('Organization:', org.name);
   *     console.log('Members:', org.members);
   *   });
   * ```
   */
  getOrganizationData(id: string): Observable<OrganizationPopulated> {
    return this.getOrganizationById(id).pipe(
      map(response => response.data)
    );
  }

  /**
   * Check if user is a member of an organization
   * 
   * @param organizationId - Organization ID to check
   * @returns Observable of boolean indicating membership status
   * 
   * @remarks
   * This method attempts to fetch the organization. If successful, the user
   * is a member. If it returns a 403 error, the user is not a member.
   * 
   * @example
   * ```typescript
   * this.organizationService.isMember('507f1f77bcf86cd799439011')
   *   .subscribe(isMember => {
   *     if (isMember) {
   *       console.log('User has access to this organization');
   *     } else {
   *       console.log('User is not a member');
   *     }
   *   });
   * ```
   */
  isMember(organizationId: string): Observable<boolean> {
    return this.getOrganizationById(organizationId).pipe(
      map(() => true),
      catchError((error) => {
        if (error.status === 403) {
          return [false];
        }
        // Re-throw other errors
        return throwError(() => error);
      })
    );
  }

  /**
   * Get count of user's organizations
   * 
   * @param params - Optional query parameters
   * @returns Observable of organization count
   * 
   * @remarks
   * Returns the count of organizations matching the query parameters.
   * 
   * @example
   * ```typescript
   * this.organizationService.getOrganizationsCount().subscribe(count => {
   *   console.log(`You have created ${count} organizations`);
   * });
   * ```
   */
  getOrganizationsCount(params?: OrganizationQueryParams): Observable<number> {
    return this.getAllOrganizations(params).pipe(
      map(response => response.count || 0)
    );
  }

  // ============================================================================
  // PRIVATE HELPER METHODS
  // ============================================================================

  /**
   * Build HTTP params from query parameters object
   * 
   * @param params - Query parameters
   * @returns HttpParams instance
   */
  private buildHttpParams(params?: Record<string, any>): HttpParams {
    let httpParams = new HttpParams();

    if (!params) {
      return httpParams;
    }

    Object.keys(params).forEach(key => {
      const value = params[key];
      
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          // Handle array parameters
          value.forEach(item => {
            httpParams = httpParams.append(key, String(item));
          });
        } else {
          httpParams = httpParams.set(key, String(value));
        }
      }
    });

    return httpParams;
  }

  /**
   * Handle HTTP errors with proper error transformation
   * 
   * @param operation - Name of the operation that failed
   * @param error - HTTP error response
   * @returns Observable that throws formatted error
   */
  private handleError(operation: string, error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An unknown error occurred';

    if (error.error instanceof ErrorEvent) {
      // Client-side or network error
      errorMessage = `Client error: ${error.error.message}`;
    } else {
      // Backend error
      if (error.error?.message) {
        errorMessage = error.error.message;
      } else if (error.status) {
        errorMessage = `Server error: ${error.status} - ${error.statusText}`;
      }
    }

    this.logDebug(`${operation} failed`, { error: errorMessage, status: error.status });

    return throwError(() => ({
      operation,
      message: errorMessage,
      status: error.status,
      originalError: error
    }));
  }

  /**
   * Log debug information if debug mode is enabled
   * 
   * @param message - Debug message
   * @param data - Optional data to log
   */
  private logDebug(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[OrganizationService] ${message}`, data || '');
    }
  }
}
